import { action, makeObservable, observable } from "mobx";
import VendorPaymentsAPI from "../api/VendorPaymentsAPI";
import {
  normalizeVendorPaymentRow,
  VendorPaymentFormPayload,
  VendorPaymentRow,
} from "../models/PurchaseRequest/VendorPayments";

export class VendorPaymentsStore {
  vendorPaymentsAPI: VendorPaymentsAPI;
  payments: VendorPaymentRow[];
  loading: boolean;
  popupState: { open: boolean; payment: VendorPaymentRow | null };
  deleteState: { open: boolean; paymentId: number | null };
  saving: boolean;
  deleting: boolean;

  constructor() {
    this.vendorPaymentsAPI = new VendorPaymentsAPI();
    this.payments = [];
    this.loading = false;
    this.popupState = { open: false, payment: null };
    this.deleteState = { open: false, paymentId: null };
    this.saving = false;
    this.deleting = false;

    makeObservable(this, {
      payments: observable,
      loading: observable,
      popupState: observable,
      deleteState: observable,
      saving: observable,
      deleting: observable,
      getVendorPayments: action,
      openCreatePopup: action,
      openEditPopup: action,
      closePopup: action,
      openDeletePopup: action,
      closeDeletePopup: action,
      saveVendorPayment: action,
      deleteVendorPayment: action,
      resetState: action,
    });
  }

  getVendorPayments = async (prId: number) => {
    this.loading = true;
    try {
      const response = await this.vendorPaymentsAPI.getVendorPayments(prId);
      this.payments = response.list
        .filter((item) => Number(item.active) === 1)
        .map((item) => normalizeVendorPaymentRow(item));
    } catch (error) {
      throw error;
    } finally {
      this.loading = false;
    }
  };

  openCreatePopup = () => {
    this.popupState = { open: true, payment: null };
  };

  openEditPopup = (payment: VendorPaymentRow) => {
    this.popupState = { open: true, payment };
  };

  closePopup = () => {
    this.popupState = { open: false, payment: null };
  };

  openDeletePopup = (paymentId: number) => {
    this.deleteState = { open: true, paymentId };
  };

  closeDeletePopup = () => {
    this.deleteState = { open: false, paymentId: null };
  };

  saveVendorPayment = async (
    payload: VendorPaymentFormPayload,
    paymentId?: number,
  ) => {
    this.saving = true;
    try {
      const response = paymentId
        ? await this.vendorPaymentsAPI.updateVendorPayment(paymentId, payload)
        : await this.vendorPaymentsAPI.createVendorPayment(payload);
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.saving = false;
    }
  };

  deleteVendorPayment = async (paymentId: number) => {
    this.deleting = true;
    try {
      const response =
        await this.vendorPaymentsAPI.deleteVendorPayment(paymentId);
      this.payments = this.payments.filter(
        (payment) => payment.id !== paymentId,
      );
      return response;
    } catch (error) {
      throw error;
    } finally {
      this.deleting = false;
    }
  };

  resetState = () => {
    this.payments = [];
    this.loading = false;
    this.popupState = { open: false, payment: null };
    this.deleteState = { open: false, paymentId: null };
    this.saving = false;
    this.deleting = false;
  };
}
